import { useQueryParams } from 'use-query-params'
import React, { Fragment, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { LntButton, LntDropdown } from '@app/components/base'
import { Icon, Show } from '@app/components/util'
import { ContentLayout } from '@app/layouts'
import { RemoveListModal } from '@app/modals/RemoveListModal'
import { CreateListModal, RegistryTotalModal } from '@app/modals'
import { ExtendedTable, PermissionWrapper, TableSettingsToolbar, SubheaderToolbarDivider } from '@app/components'
import {
  useToggle,
  useTableSearch,
  useNotification,
  useExtendedNavigate,
  usePaginationQueryParams,
  useMultiStatusMutation,
  useTableSetting,
  useTableSize
} from '@app/hooks'

import { exportMonitoringListsFile } from '@app/utils/export'
import { getExportFilesDropdownMenu } from '@app/utils'
import {
  getLists,
  deleteLists,
  copyListsItemProducts,
  getMonitoringListsSummary,
  approveLists
} from '@app/api/routes/monitoringLists'

import { FILE_TYPES } from '@app/api/constants'
import { ICON_NAME } from '@app/constants/icon'
import { REGISTRY_PAGE } from '@app/constants/page'
import { BUTTON_TYPE, SIZE } from '@app/constants'
import { MONITORINGS_LISTS_KEY } from '@app/constants/queryKeys'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'

import { PERMISSION } from '@app/constants/permission'

import { TopFilters } from './ui'
import { getColumns, adaptGetListsParams, adaptRegistryCurrentListsTableData } from './lib'
import { columnsSearchSchema, topFiltersQuerySchema, tableSearchQuerySchema } from './configs'

import type { DefaultParams } from '@app/api/types'
import type { FiltersParams } from '@app/types/filter'
import type { Key, ReactNode } from 'react'
import type { CreateListsData, GetListsParams } from '@app/api/routes/monitoringLists/types'
import type { FileType } from '@app/types'

const columns = getColumns()

export const CurrentListsTab: React.FC = () => {
  const navigate = useExtendedNavigate()
  const queryClient = useQueryClient()
  const notification = useNotification()

  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()
  const [topFiltersQuery, setTopFiltersQuery] = useQueryParams(topFiltersQuerySchema)

  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const { size, onSizeToggle } = useTableSize(['middle', 'large'])

  const copyListModalToggle = useToggle()
  const removeListModalToggle = useToggle()
  const registryTotalModalToggle = useToggle()

  const buttonLoadingExportAllToggle = useToggle()
  const buttonLoadingExportToggle = useToggle()

  const listsDataParams = useMemo(
    () => ({ ...paginationParams, ...topFiltersQuery }),
    [paginationParams, topFiltersQuery]
  ) as GetListsParams & DefaultParams

  const {
    data: registryCurrentListsTableData,
    refetch: listsDataRefetch,
    isLoading: isRegistryCurrentListsDataLoadin
  } = useQuery({
    queryKey: [MONITORINGS_LISTS_KEY.currentListItems, listsDataParams],
    queryFn: async () => await getLists(listsDataParams),
    select: registryCurrentListsData => adaptRegistryCurrentListsTableData(registryCurrentListsData)
  })

  const { data: listSummaryData } = useQuery({
    queryKey: [MONITORINGS_LISTS_KEY.listsSummary, listsDataParams],
    queryFn: async () => await getMonitoringListsSummary(listsDataParams)
  })

  const deleteListsMutation = useMutation({
    mutationFn: deleteLists,
    onSuccess: async () => {
      notification.success({
        message: 'Успешно:',
        description: 'Список успешно удален!'
      })

      await listsDataRefetch()
    }
  })

  const { mutation: approveListsMutation, modalContextHolder } = useMultiStatusMutation({
    mutationFn: approveLists,
    onSuccess: async () => {
      notification.success({
        message: 'Успешно:',
        description: 'Списки согласованы!'
      })

      await listsDataRefetch()
    }
  })

  const copyListMutation = useMutation({
    mutationFn: copyListsItemProducts,
    onSuccess: async () => await queryClient.invalidateQueries({ queryKey: [MONITORINGS_LISTS_KEY.currentListItems] })
  })

  const handleRemoveList = (): void => {
    deleteListsMutation.mutate({ ids: selectedIds }, { onSettled: removeListModalToggle.setUntoggled })
  }

  const handleNavigateToInnerPageBySelected = (): void => {
    const id = selectedIds[0]

    navigate(`${REGISTRY_PAGE.registry}/${id}`)
  }

  const handleNavigateToInnerPage = (key?: number): void => {
    if (!key) return

    navigate(`${REGISTRY_PAGE.registry}/${key}`)
  }

  const handleApprove = (): void => {
    approveListsMutation.mutate({ monitoringListIds: selectedIds })
  }

  const handlePaginationChange = (current: number, pageSize: number): void => {
    setPagination({ current, pageSize })
  }

  const handleFiltersChange = (filters: FiltersParams): void => {
    setTopFiltersQuery(filters)
    resetCurrentPage()
  }

  const handleSelectedRowChange = (selectedKeys: Key[]): void => {
    setSelectedIds(selectedKeys.map(Number))
  }

  const handleCopyList = (data: CreateListsData): void => {
    const monitoringListId = selectedIds[0]

    copyListMutation.mutate({ monitoringListId, ...data }, { onSettled: copyListModalToggle.setUntoggled })
  }

  const handleExportListClick = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportToggle.setToggled()

    const fileName = 'Список_товаров'
    try {
      await exportMonitoringListsFile(fileName, { ids: selectedIds, file: fileType })
    } finally {
      buttonLoadingExportToggle.setUntoggled()
    }
  }

  const handleExportAllClick = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportAllToggle.setToggled()

    const data = {
      ...adaptGetListsParams(topFiltersQuery),
      file: fileType
    }

    const fileName = 'Список_товаров'

    try {
      await exportMonitoringListsFile(fileName, data)
    } finally {
      buttonLoadingExportAllToggle.setUntoggled()
    }
  }

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.currentListsTab, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredRegistryCurrentListsDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: registryCurrentListsTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const exportDropdownItems = getExportFilesDropdownMenu(
    'Список товаров',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportListClick
  )
  const exportAllDropdownItems = getExportFilesDropdownMenu(
    'Список товаров',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportAllClick
  )

  const toolbar: ReactNode[] = [
    <Show
      key='view_change_copy-show'
      condition={ selectedIds.length === 1 }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleNavigateToInnerPageBySelected }
      >
        Просмотр
      </LntButton>

      <PermissionWrapper requiredPermissions={ [PERMISSION.recommendationToChangeMonitoringList] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ handleNavigateToInnerPageBySelected }
        >
          Изменить
        </LntButton>
      </PermissionWrapper>

      <SubheaderToolbarDivider />
    </Show>,

    <Show
      key='export-list_approve_delete-button'
      condition={ selectedIds.length >= 1 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.createAndCopyMonitoringList] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ copyListModalToggle.setToggled }
        >
          Копировать
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.approveMonitoringList] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ handleApprove }
        >
          Согласовать
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.deleteMonitoringList] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ removeListModalToggle.setToggled }
        >
          Удалить
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.exportMonitoringList] }>
        <LntDropdown
          trigger={ ['click'] }
          items={ exportDropdownItems }
        >
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            loading={ buttonLoadingExportToggle.value }
          >
            Экспорт
            <Icon name={ ICON_NAME.arrowDown } />
          </LntButton>
        </LntDropdown>
      </PermissionWrapper>

      <SubheaderToolbarDivider />
    </Show>,

    <PermissionWrapper
      key='export-list_button'
      requiredPermissions={ [PERMISSION.exportMonitoringList] }
    >
      <LntDropdown
        trigger={ ['click'] }
        items={ exportAllDropdownItems }
      >
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          loading={ buttonLoadingExportAllToggle.value }
        >
          Экспорт всех
          <Icon name={ ICON_NAME.arrowDown } />
        </LntButton>
      </LntDropdown>
    </PermissionWrapper>,

    <Fragment key='total-list_changes-buttons'>
      <PermissionWrapper requiredPermissions={ [PERMISSION.viewMonitoringListsRegistry] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ registryTotalModalToggle.setToggled }
        >
          Итого по спискам
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.recommendationToChangeMonitoringList] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          disabled
        >
          Изменения
          <Icon name={ ICON_NAME.arrowDown } />
        </LntButton>
      </PermissionWrapper>

      <SubheaderToolbarDivider />
    </Fragment>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSizeButtonProps={ { active: size === SIZE.large, onClick: onSizeToggle } }
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <>
      <ContentLayout>
        <TopFilters
          onSearch={ handleFiltersChange }
          initialFilters={ topFiltersQuery as FiltersParams }
        />

        <ExtendedTable
          size={ size }
          loading={ isRegistryCurrentListsDataLoadin }
          columns={ columnsWithSearchTool }
          dataSource={ filteredRegistryCurrentListsDataSource }
          toolbar={ toolbar }
          rowSelection={ {
            onChange: handleSelectedRowChange
          } }
          pagination={ {
            ...pagination,
            total: registryCurrentListsTableData?.count,
            onChange: handlePaginationChange
          } }
          onRowDoubleClick={ (_, record) => handleNavigateToInnerPage(record.key) }
        />
      </ContentLayout>

      <RemoveListModal
        title='Вы действительно хотите удалить выбранные позиции?'
        open={ removeListModalToggle.value }
        onCancel={ removeListModalToggle.setUntoggled }
        onRemoveList={ handleRemoveList }
      />

      <CreateListModal
        title='Копировать список'
        open={ copyListModalToggle.value }
        onCancel={ copyListModalToggle.setUntoggled }
        onCreateList={ handleCopyList }
      />

      <RegistryTotalModal
        title='Итого по спискам'
        summaryEntity={ listSummaryData }
        open={ registryTotalModalToggle.value }
        onCancel={ registryTotalModalToggle.setUntoggled }
      />

      { modalContextHolder }
    </>
  )
}
